import React, { useState } from "react";
import { toast } from "react-toastify";
import {
  FaTimes,
  FaMoneyBillWave,
  FaMobile,
  FaUniversity,
} from "react-icons/fa";

const WithdrawalModal = ({ isOpen, onClose, onSubmit, maxAmount }) => {
  const [withdrawalData, setWithdrawalData] = useState({
    amount: "",
    paymentMethod: "upi",
    accountDetails: {
      upiId: "",
      accountNumber: "",
      ifscCode: "",
      accountHolderName: "",
      bankName: "",
    },
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    // Amount validation
    if (!withdrawalData.amount || withdrawalData.amount <= 0) {
      newErrors.amount = "Please enter a valid amount";
    } else if (parseFloat(withdrawalData.amount) > maxAmount) {
      newErrors.amount = "Amount exceeds available balance";
    } else if (parseFloat(withdrawalData.amount) < 100) {
      newErrors.amount = "Minimum withdrawal amount is ₹100";
    }

    // Payment method specific validation
    if (withdrawalData.paymentMethod === "upi") {
      if (!withdrawalData.accountDetails.upiId) {
        newErrors.upiId = "UPI ID is required";
      } else if (
        !/^[\w.-]+@[\w.-]+$/.test(withdrawalData.accountDetails.upiId)
      ) {
        newErrors.upiId = "Please enter a valid UPI ID";
      }
    } else if (withdrawalData.paymentMethod === "bank_transfer") {
      if (!withdrawalData.accountDetails.accountNumber) {
        newErrors.accountNumber = "Account number is required";
      }
      if (!withdrawalData.accountDetails.ifscCode) {
        newErrors.ifscCode = "IFSC code is required";
      }
      if (!withdrawalData.accountDetails.accountHolderName) {
        newErrors.accountHolderName = "Account holder name is required";
      }
      if (!withdrawalData.accountDetails.bankName) {
        newErrors.bankName = "Bank name is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onSubmit({
      amount: parseFloat(withdrawalData.amount),
      paymentMethod: withdrawalData.paymentMethod,
      accountDetails: withdrawalData.accountDetails,
    });

    // Reset form
    setWithdrawalData({
      amount: "",
      paymentMethod: "upi",
      accountDetails: {
        upiId: "",
        accountNumber: "",
        ifscCode: "",
        accountHolderName: "",
        bankName: "",
      },
    });
    setErrors({});
  };

  const handleInputChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setWithdrawalData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setWithdrawalData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">Request Withdrawal</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center mb-2">
                <FaMoneyBillWave className="w-5 h-5 text-blue-600 mr-2" />
                <span className="font-medium text-blue-800">
                  Available Balance
                </span>
              </div>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(maxAmount)}
              </p>
            </div>
          </div>

          {/* Amount Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Withdrawal Amount *
            </label>
            <div className="relative">
              <span className="absolute left-3 top-3 text-gray-500">₹</span>
              <input
                type="number"
                value={withdrawalData.amount}
                onChange={(e) => handleInputChange("amount", e.target.value)}
                className={`w-full pl-8 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.amount ? "border-red-300" : "border-gray-300"
                }`}
                placeholder="Enter amount"
                min="100"
                max={maxAmount}
              />
            </div>
            {errors.amount && (
              <p className="text-red-500 text-sm mt-1">{errors.amount}</p>
            )}
            <p className="text-sm text-gray-500 mt-1">
              Minimum withdrawal: ₹100
            </p>
          </div>

          {/* Payment Method Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Payment Method *
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleInputChange("paymentMethod", "upi")}
                className={`p-4 border rounded-lg flex flex-col items-center transition-colors ${
                  withdrawalData.paymentMethod === "upi"
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 hover:border-gray-400"
                }`}
              >
                <FaMobile className="w-6 h-6 mb-2 text-purple-600" />
                <span className="text-sm font-medium">UPI</span>
              </button>
              <button
                type="button"
                onClick={() =>
                  handleInputChange("paymentMethod", "bank_transfer")
                }
                className={`p-4 border rounded-lg flex flex-col items-center transition-colors ${
                  withdrawalData.paymentMethod === "bank_transfer"
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 hover:border-gray-400"
                }`}
              >
                <FaUniversity className="w-6 h-6 mb-2 text-blue-600" />
                <span className="text-sm font-medium">Bank Transfer</span>
              </button>
            </div>
          </div>

          {/* UPI Details */}
          {withdrawalData.paymentMethod === "upi" && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                UPI ID *
              </label>
              <input
                type="text"
                value={withdrawalData.accountDetails.upiId}
                onChange={(e) =>
                  handleInputChange("accountDetails.upiId", e.target.value)
                }
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.upiId ? "border-red-300" : "border-gray-300"
                }`}
                placeholder="yourname@paytm"
              />
              {errors.upiId && (
                <p className="text-red-500 text-sm mt-1">{errors.upiId}</p>
              )}
            </div>
          )}

          {/* Bank Transfer Details */}
          {withdrawalData.paymentMethod === "bank_transfer" && (
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Holder Name *
                </label>
                <input
                  type="text"
                  value={withdrawalData.accountDetails.accountHolderName}
                  onChange={(e) =>
                    handleInputChange(
                      "accountDetails.accountHolderName",
                      e.target.value,
                    )
                  }
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.accountHolderName
                      ? "border-red-300"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter account holder name"
                />
                {errors.accountHolderName && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.accountHolderName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Account Number *
                </label>
                <input
                  type="text"
                  value={withdrawalData.accountDetails.accountNumber}
                  onChange={(e) =>
                    handleInputChange(
                      "accountDetails.accountNumber",
                      e.target.value,
                    )
                  }
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.accountNumber ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="Enter account number"
                />
                {errors.accountNumber && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.accountNumber}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  IFSC Code *
                </label>
                <input
                  type="text"
                  value={withdrawalData.accountDetails.ifscCode}
                  onChange={(e) =>
                    handleInputChange("accountDetails.ifscCode", e.target.value)
                  }
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.ifscCode ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="Enter IFSC code"
                />
                {errors.ifscCode && (
                  <p className="text-red-500 text-sm mt-1">{errors.ifscCode}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bank Name *
                </label>
                <input
                  type="text"
                  value={withdrawalData.accountDetails.bankName}
                  onChange={(e) =>
                    handleInputChange("accountDetails.bankName", e.target.value)
                  }
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.bankName ? "border-red-300" : "border-gray-300"
                  }`}
                  placeholder="Enter bank name"
                />
                {errors.bankName && (
                  <p className="text-red-500 text-sm mt-1">{errors.bankName}</p>
                )}
              </div>
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Submit Request
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WithdrawalModal;
